import * as _ from 'lodash';
import { ElementType, IQuestionConfig } from "src/app/ui-testrunner/models";
import { ItemSetFrameworkCtrl } from "../../item-set-editor/controllers/framework";
import { ItemBankCtrl } from "../../item-set-editor/controllers/item-bank";
import { TestFormConstructionMethod } from "../../item-set-editor/models/assessment-framework";
import { AuditQuestionScope, ITEM_MAP_MODULE_META_AUDIT_CONST_LABELS, SubAuditExcludedItems } from "../data/audits";
import { InputFormat } from 'src/app/ui-testrunner/element-render-input/model';
import { getQuestionDeepElements } from './content-analysis';
import { deepFindAll } from '../../services/util';

type PromiseQuestionList = Promise<IQuestionConfig[]>

export interface IContext {
    lang: string,
    itemBankCtrl: ItemBankCtrl, // todo: highly stateful
    frameworkCtrl: ItemSetFrameworkCtrl, // todo: highly stateful
}

export const getQuestionsByScope = async (questionScope: AuditQuestionScope, ctx:IContext, languageSensitive:boolean = true) => {
  return getQuestionsByScopeSync(questionScope, ctx, languageSensitive);
};

/**
 * Same scope rules as `getQuestionsByScope`, resolved synchronously so template
 * bindings and filter pickers can use them without an await.
 */
export function getQuestionsByScopeSync(questionScope: AuditQuestionScope, ctx:IContext, languageSensitive:boolean = true): IQuestionConfig[] {
  switch (questionScope){
    case AuditQuestionScope.ITEMS_SCORED           : return getItemsScored(ctx);
    case AuditQuestionScope.ITEMS_SCORED_MINUS_FT  : return getItemsScoredMinusFT(ctx);
    case AuditQuestionScope.ITEMS_SCORED_MINUS_HS  : return getItemsScoredMinusHumanScored(ctx);
    case AuditQuestionScope.ITEMS_HUMAN_SCORED     : return getItemsHumanScored(ctx);
    case AuditQuestionScope.ITEMS_SURVEY           : return getItemsSurvey(ctx);
    case AuditQuestionScope.SCREENS_NONSCORED      : return []; // todo
    case AuditQuestionScope.SCREENS_PASSAGES       : return getScreensPassages(ctx);
    case AuditQuestionScope.SCREENS_NON_PASSAGES   : return getNonReadingPassageItems(ctx);
    case AuditQuestionScope.SCREENS_INCLUDE_DISABLED: return getScreens(ctx, questionScope, languageSensitive)
    case AuditQuestionScope.ITEM_BANK              : return ctx.frameworkCtrl.itemBankCtrl.getItems().map(q => getQuestionByLang(q, ctx.lang));
    case AuditQuestionScope.ITEM_BANK_SCORED       : return ctx.frameworkCtrl.itemBankCtrl.getItems().filter(q => !q.isReadingSelectionPage && !q.isQuestionnaire).map(q => languageSensitive?getQuestionByLang(q, ctx.lang):q);
    case AuditQuestionScope.ITEM_BANK_PASSAGES     : return ctx.frameworkCtrl.itemBankCtrl.getItems().filter(q => q.isReadingSelectionPage).map(q => languageSensitive?getQuestionByLang(q, ctx.lang):q);
    case AuditQuestionScope.PASSAGE_ITEMS       : return getPassageTestletItems(ctx);
    case AuditQuestionScope.SCREENS_PASSAGES_MAP_MODULE       : return getScreensPassagesMapModule(ctx);
    case AuditQuestionScope.SCREENS_SCORE_MATRIX_VALIDATED: return getScreens(ctx).filter(question => ctx.itemBankCtrl.isValidationEnabled(true, question))
    case AuditQuestionScope.NONE       : return [];
    default:
    case AuditQuestionScope.SCREENS                : return getScreens(ctx, questionScope,languageSensitive);
  }
  // Made sceens language sensitive since the rest depend on it, right now everything will be language sensitive
}
// todo need to make it so that they aren't all dependent on SCREENS as they all use getScreens to get their questions
// todo and go from there some require item bank but item bank can benifit from the additional filtering and can help to string filtering together

export interface IAuditScopeOption {
  id: AuditQuestionScope;
  label: string;
  /** Picker grouping — test-design scoped sets vs the raw bank. */
  group: string;
}

/**
 * The audit scopes worth offering as a one-click filter on the item listing.
 * `ITEMS_SCORED` is the default everywhere: items placed in the test design,
 * disabled testlets excluded, minus reading passages and questionnaires.
 */
export const DEFAULT_AUDIT_SCOPE = AuditQuestionScope.ITEMS_SCORED;

export const AUDIT_SCOPE_FILTER_OPTIONS: IAuditScopeOption[] = [
  { id: AuditQuestionScope.ITEMS_SCORED,            group: 'Test design', label: 'Active items (no passages or questionnaires)' },
  { id: AuditQuestionScope.ITEMS_SCORED_MINUS_FT,   group: 'Test design', label: 'Active items, excluding field trial' },
  { id: AuditQuestionScope.ITEMS_HUMAN_SCORED,      group: 'Test design', label: 'Human-scored items' },
  { id: AuditQuestionScope.ITEMS_SCORED_MINUS_HS,   group: 'Test design', label: 'Active items, excluding human-scored' },
  { id: AuditQuestionScope.ITEMS_SURVEY,            group: 'Test design', label: 'Questionnaire items' },
  { id: AuditQuestionScope.SCREENS_PASSAGES,        group: 'Test design', label: 'Reading passages' },
  { id: AuditQuestionScope.PASSAGE_ITEMS,           group: 'Test design', label: 'Items in passage sets' },
  { id: AuditQuestionScope.SCREENS,                 group: 'Test design', label: 'All screens' },
  { id: AuditQuestionScope.SCREENS_INCLUDE_DISABLED,group: 'Test design', label: 'All screens, including disabled' },
  { id: AuditQuestionScope.ITEM_BANK_SCORED,        group: 'Item bank',   label: 'Item bank — scored items' },
  { id: AuditQuestionScope.ITEM_BANK_PASSAGES,      group: 'Item bank',   label: 'Item bank — passages' },
  { id: AuditQuestionScope.ITEM_BANK,               group: 'Item bank',   label: 'Whole item bank' },
];

export function buildItemScopeContext(
  frameworkCtrl: ItemSetFrameworkCtrl,
  itemBankCtrl: ItemBankCtrl,
  lang = 'en',
): IContext {
  return { frameworkCtrl, itemBankCtrl, lang };
}

/** Sync test-design scope — same rules as audits (`extractTestDesignScopedquestion`). */
export function resolveTestDesignScopedQuestionsMap(
  ctx: IContext,
  questionScope?: string,
): Map<number, IQuestionConfig> {
  const { frameworkCtrl } = ctx;
  const asmtFmrk = frameworkCtrl.asmtFmrk;
  let questionsMap: Map<number, IQuestionConfig> = new Map();
  const excludeDisabled = questionScope !== AuditQuestionScope.SCREENS_INCLUDE_DISABLED;

  switch (asmtFmrk.testFormType) {
    case TestFormConstructionMethod.TLOFT:
      questionsMap = getTloftQuestions(ctx, excludeDisabled);
      break;
    case TestFormConstructionMethod.MSCAT:
      frameworkCtrl.panelCtrl
        ?.getAllMsCatQuestions(true, excludeDisabled)
        .forEach((q) => questionsMap.set(q.id, q));
      break;
    default:
    case TestFormConstructionMethod.LINEAR:
      questionsMap = getLinearFormQuestions(ctx);
      break;
    // case TestFormConstructionMethod.BUCKETS:
  }

  return questionsMap;
}

export const extractTestDesignScopedquestion = async (ctx: IContext, questionScope?: string) => {
  const { lang } = ctx;
  const questionsMap = resolveTestDesignScopedQuestionsMap(ctx, questionScope);
  return { questionsMap, lang };
};

export const applyItemFilter = (items:IQuestionConfig[], excludedItems:SubAuditExcludedItems[] = [], ctx: IContext): IQuestionConfig[] => {
  let filteredItems = items.filter(item => !!item);
  if(excludedItems.includes("QUESTIONNAIRE")){
    filteredItems = filteredItems.filter(item =>{
      return !item.isQuestionnaire
    })
  }
  if(excludedItems.includes("READING_SELECTIONS")){
    filteredItems = filteredItems.filter(item =>{
      return !item.isReadingSelectionPage
    })
  }
  if(excludedItems.includes("FT")){
    filteredItems = filteredItems.filter(item =>{
      return !item.meta?.FT;
    })
  }
  if(excludedItems.includes("NON_FT")){
    filteredItems = filteredItems.filter(item =>{
      return !!item.meta?.FT;
    })
  }
  if(excludedItems.includes("WRITING_WITHOUT_ASSOCIATED_RESOURCE")){
    filteredItems = filteredItems.filter(item =>{
      return !item.readSelections?.length && deepFindAll(item.content,'elementType', ElementType.INPUT).some(content => content.format === InputFormat.TEXT);
    })
  }
  if(excludedItems.includes("NON_READING_SELECTIONS")){
    filteredItems = filteredItems.filter(item =>{
      return item.isReadingSelectionPage
    })
  }
  if(excludedItems.includes("NO_RESOURCE_OR_SELECTION")){
    filteredItems = filteredItems.filter(item =>{
      return item.readSelections ? item.readSelections.length > 0 : true;
    })
  }
  if(excludedItems.includes('ALTERNATIVE')){
    filteredItems = filteredItems.filter(item =>{
      return !item?.meta?.question_id_reg && !item?.meta?.ALT;
    })
  }
  if(excludedItems.includes('NON_ALTERNATIVE')){
    filteredItems = filteredItems.filter(item =>{
      return item?.meta?.question_id_reg || item?.meta?.ALT;
    })
  }
  if(excludedItems.includes('NON_HUMAN_SCORED')){
    filteredItems = filteredItems.filter( item => {
      return checkQuestionInScope(item, AuditQuestionScope.ITEMS_HUMAN_SCORED, ctx)
    })
  }
  if(excludedItems.includes('HUMAN_SCORED')){
    filteredItems = filteredItems.filter( item => {
      return !checkQuestionInScope(item, AuditQuestionScope.ITEMS_HUMAN_SCORED, ctx)
    })
  }
  return filteredItems
}

export const checkQuestionInScope =  (
  question: IQuestionConfig, 
  questionScope: AuditQuestionScope, 
  ctx: IContext, 
): boolean => {

  // Always return true by default
  if (
    questionScope === AuditQuestionScope.SCREENS || 
    questionScope === AuditQuestionScope.SCREENS_INCLUDE_DISABLED || 
    questionScope === AuditQuestionScope.ITEM_BANK
  ) {
    return true;
  }

  switch (questionScope) {
    case AuditQuestionScope.ITEMS_SCORED:
      return !question.isReadingSelectionPage && !question.isQuestionnaire;

    case AuditQuestionScope.ITEMS_SCORED_MINUS_FT:
      return !question.meta || (question.meta && !question.meta['FT']);

    case AuditQuestionScope.ITEMS_SCORED_MINUS_HS: {
      const isScored = checkQuestionInScope(question, AuditQuestionScope.ITEMS_SCORED, ctx)
      if(!isScored){
        return false
      }
      const scoringInfo = ctx.itemBankCtrl.getQuestionScoringInfo(+question.id);
      return !scoringInfo?.is_human_scored;
    }

    case AuditQuestionScope.ITEMS_HUMAN_SCORED: {
      const scoringConfig = ctx.itemBankCtrl.getQuestionScoringInfo(+question.id);
      const qContent = getQuestionDeepElements(question);
      const containsTextBox = qContent.some(content => 
        content.elementType === ElementType.INPUT && content.format === InputFormat.TEXT
      );
      return containsTextBox || scoringConfig?.is_human_scored;
    }

    case AuditQuestionScope.ITEMS_SURVEY:
      return ctx.frameworkCtrl.asmtFmrk.isQuestionnaireAssessment || question.isQuestionnaire;

    case AuditQuestionScope.SCREENS_NONSCORED:
      return question.isReadingSelectionPage || question.isQuestionnaire;

    case AuditQuestionScope.SCREENS_PASSAGES:
      return question.isReadingSelectionPage;

    case AuditQuestionScope.SCREENS_NON_PASSAGES:
      return !question.isReadingSelectionPage;

    case AuditQuestionScope.ITEM_BANK_SCORED:
      return !question.isReadingSelectionPage && !question.isQuestionnaire;

    case AuditQuestionScope.ITEM_BANK_PASSAGES:
      return question.isReadingSelectionPage;

    case AuditQuestionScope.PASSAGE_ITEMS: {
      const passageIds = new Set(
        getPassageTestletItems(ctx).map(q => +q.id)
      );
      return passageIds.has(+question.id) || !!question.readSelections?.length;
    }

    default:
      return true;
  }
};

// get all tloft questions
const getTloftQuestions = (ctx:IContext, excludeDisabled:boolean = true): Map<number, IQuestionConfig> => {
  let { itemBankCtrl, frameworkCtrl} = ctx;
  const asmtFmrk = frameworkCtrl.asmtFmrk;
  const questionsMap: Map<number, IQuestionConfig> = new Map();
  const prePostAmbleQid: Set<number> = new Set();
  // build prepostamble list
  const storePrePostAmbleByLabel = (qLabel:string) => {
    const q = (itemBankCtrl.getQuestionByLabel(qLabel));
    if (q){ prePostAmbleQid.add(+q.id) }
  }
  asmtFmrk.partitions.forEach(partition => {
    partition?.preambleLabelList?.forEach(qLabel => storePrePostAmbleByLabel(qLabel) );
    partition?.postambleLabelList?.forEach(qLabel => storePrePostAmbleByLabel(qLabel) );
  })

  const questionIds = _.flattenDeep(
    asmtFmrk.testlets.filter(testlet => !excludeDisabled || !testlet.isDisabled).map(
      testlet => testlet.questions.map(question => question.id) 
    )
  );

  questionIds?.forEach((id) => {
    if (!prePostAmbleQid.has(+id)) {
      const question = itemBankCtrl.getQuestionById(+id);
      if (question) {
        questionsMap.set(+id, question);
      }
    }
  });

  return questionsMap;
}

const getMSCATQuestions = async (ctx:IContext): Promise<Map<number, IQuestionConfig>>  => { // todo: is this assuming msCAT?
  let {lang, itemBankCtrl, frameworkCtrl} = ctx;
  const asmtFmrk = frameworkCtrl.asmtFmrk;
  let questionsMap: Map<number, IQuestionConfig> = new Map();
  const prePostAmbleQid: Set<number> = new Set();
  
  const testForms = await frameworkCtrl.publishingCtrl.getTestForms();
  for(const testForm of testForms){
    const form = JSON.parse(testForm);
    const {lang: testLang, questionDb, sections}  = form
    lang = testLang;
    //build pre-post amble Set
    sections.forEach(section => {
      if(section?.preambleList) section.preambleList?.forEach(qId => prePostAmbleQid.add(+qId))  ;
      if(section?.postambleList) section.postambleList?.forEach(qId => prePostAmbleQid.add(+qId)) ;
    })
    Object.keys(questionDb).map( async (qId:string | number) => {
      if(!prePostAmbleQid.has(+qId)) {
        let question = itemBankCtrl.getQuestionById(+qId);
        if(!question){
          question = await itemBankCtrl.saveLoadCtrl.loadDocument(new Map(), +qId, true); // forcing outermost structure
        } 
        questionsMap.set(+qId, question);
        // if(!this.isRejectedItem(question)) 
      }
    });

  }
  return questionsMap
}

const getLinearFormQuestions = (ctx:IContext): Map<number, IQuestionConfig> => {
  const questionsMap: Map<number, IQuestionConfig> = new Map();
  let { itemBankCtrl, frameworkCtrl} = ctx;
  const asmtFmrk = frameworkCtrl.asmtFmrk;
  const sections = frameworkCtrl.asmtFmrk.partitions;
  sections.forEach(section =>{
    const sectionItems = asmtFmrk.sectionItems[section.id] || asmtFmrk.sectionItems[section.id + ''];
    sectionItems?.questions?.forEach(question =>{
      questionsMap.set(question.id,itemBankCtrl.getQuestionById(question.id));
    })
  });
  return questionsMap
}


// fetches all the screens (which includes items)
export const getScreens = (ctx:IContext, questionScope?:string, translate:boolean = true):IQuestionConfig[] => {
  const questionsMap = resolveTestDesignScopedQuestionsMap(ctx, questionScope);
  return Array.from(questionsMap.values()).filter(q=> !!q).map(q => translate?getQuestionByLang(q, ctx.lang):q); // ensure question exists then if it needs to be translated
}

// fetches all the questions that are not reading selection pages and not questionnaires
const getItemsScored = (ctx:IContext):IQuestionConfig[] => {
  const questions = getScreens(ctx);
  return questions.filter(q => !q.isReadingSelectionPage && !q.isQuestionnaire)
}

// fetches all the questions that are not reading selection pages
const getNonReadingPassageItems = (ctx:IContext):IQuestionConfig[] => {
  const questions = getScreens(ctx);
  return questions.filter(q => !q.isReadingSelectionPage)
}

// fetches all the screens that are not scored
const getScreensNonscored = (ctx:IContext):IQuestionConfig[] => {
  const itemScoredQuestions = getItemsScored(ctx);
  const itemScoredQIds = new Set([...itemScoredQuestions.map(q => +q.id)]);
  const screens = getScreens(ctx);
  return screens.filter(screen => !itemScoredQIds.has(+screen.id) )
}

// fetches all scored questions which do not have a field trial flag set in their meta data
const getItemsScoredMinusFT = (ctx:IContext):IQuestionConfig[] => {
  const itemScoredQuestions = getItemsScored(ctx);
  return itemScoredQuestions.filter(q => !q.meta || (q.meta && !q.meta['FT'])); // todo: this prop is not actually designated as a universal embedded field trial flag, but it is sort of being used as if it is, this might be a good thing to store in the style profile
}

// fetches all questions that look like they should be human scored
const getItemsHumanScored = (ctx:IContext):IQuestionConfig[] => {
  const itemScoredQuestions = getItemsScored(ctx);
  const items = [];
  for(const item of itemScoredQuestions){
    const scoringConfig = ctx.itemBankCtrl.getQuestionScoringInfo(+item.id);
    const qContent =  getQuestionDeepElements(item);
    let containsTextBox = false // todo: this is a sub-optimal approach, assumes text is not scored directly (sometimes it is)
    qContent.forEach(content => {
      if(content.elementType == ElementType.INPUT && content.format == InputFormat.TEXT){
        containsTextBox = true
      }
    });
    if(containsTextBox || scoringConfig.is_human_scored){
      items.push(item)
    }
  }
  return items;
}

// fetches all scored questions that are not human scored
const getItemsScoredMinusHumanScored = (ctx:IContext):IQuestionConfig[] => {
  const itemsHS = getItemsHumanScored(ctx);
  const itemsHSIds = new Set([...itemsHS.map(q => +q.id)]);
  const itemsScored = getItemsScored(ctx);
  return itemsScored.filter(item => !itemsHSIds.has(+item.id) )
}

// fetches all screens that are reading selection pages
const getScreensPassagesMapModule = (ctx:IContext):IQuestionConfig[] => {
  const screens = getScreens(ctx);
  const readingSelections = screens.filter(screen => screen.isReadingSelectionPage)
  ITEM_MAP_MODULE_META_AUDIT_CONST_LABELS.forEach(questionLabel =>{
    const question = ctx.itemBankCtrl.getQuestionByLabel(questionLabel);
    if(question){
      readingSelections.push(question);
    }
  });
  return readingSelections;
}

const getScreensPassages = (ctx:IContext):IQuestionConfig[] => {
  const screens = getScreens(ctx);
  return screens.filter(screen => screen.isReadingSelectionPage);
}

// fetches all questions that are questionnaires, or if the assessment framework in context is a questionnaire assessment, it fetches all questions
const getItemsSurvey = (ctx:IContext):IQuestionConfig[] => {
  const questions = getScreens(ctx);
  if (ctx.frameworkCtrl.asmtFmrk.isQuestionnaireAssessment){ // todo: this might still be too wide
    return questions;
  }
  return questions.filter(q => q.isQuestionnaire)
}

const getPassageTestletItems = (ctx:IContext):IQuestionConfig[] => {
  const {frameworkCtrl, itemBankCtrl} = ctx;
  const sections = frameworkCtrl.asmtFmrk.sectionItems;
  const assesmentType = frameworkCtrl.testFormConstructionMethod.value;
  const questions:IQuestionConfig[] = [];
  const questionIds = new Set<IQuestionConfig>();

  // Helper function that returns populates the set of questions to ensure unique values
  const getPassageItems = (_questions: {id:number}[], ) => {
    let isPassageGroup = false;
    let groupQuestions = []
    _questions.forEach(question =>{
      let retrievedQuestion: IQuestionConfig;
      try{
        retrievedQuestion = getQuestionByLang(itemBankCtrl.getQuestionById(question.id), ctx.lang); // this has to be language sensitive as read selection depends on language
      } catch{
        console.error("Could not retrieve question with ID " + question.id + " for audit");
      }
      if(retrievedQuestion){
        groupQuestions.push(retrievedQuestion);
        if(retrievedQuestion.isReadingSelectionPage){
          isPassageGroup = true;
        }
      }
    });
    if(isPassageGroup){
      groupQuestions.forEach(q => {
        if(!questionIds.has(q.id)){
          questions.push(q);
          questionIds.add(q.id);
        }
        });
    } 
  }
  switch(assesmentType){
    case TestFormConstructionMethod.MSCAT:
    case TestFormConstructionMethod.TLOFT:
      const testlets = frameworkCtrl.asmtFmrk.testlets?.filter(testlet => !testlet.isDisabled) ?? [];
      testlets.forEach(testlet => getPassageItems(testlet.questions.map(t => {return {id: t.id}})));
      break;
    default:
    case TestFormConstructionMethod.LINEAR:
      Object.values(sections).forEach(section => {
        getPassageItems(section.questions);
      })
    // case TestFormConstructionMethod.BUCKETS:
  }
  return Array.from(questions);
}

export const getQuestionByLang = (question: IQuestionConfig, lang?): IQuestionConfig => {
  if(lang !== 'en'){
    return {...question.langLink, id: question.id, meta: question.langLink.meta?.size ? question.langLink.meta:question.meta, item_version_code: question.item_version_code};
  }
  return question ;
}